// Converted meshes (glTF with the exact node matrices in extras.wmd) posed with their exact
// `.wanim` curves: the asset viewer's loader (client/tools/viewer/viewer.js loadMesh/applyStates/
// pose) made reusable: one loaded template per mesh, cheap instances per sim object.
import * as THREE from 'three';
import { GLTFLoader, type GLTF } from 'three/addons/loaders/GLTFLoader.js';
import { clone as cloneSkinned } from 'three/addons/utils/SkeletonUtils.js';
import { CORE_CLASSES, Curve, KIND_CORE, KIND_UNIFORM, bindClip, coreFieldName, localMatrix, parseWanim, poseAt } from '../../tools/viewer/wanim.js';
import { assetBytes, assetJson, assetUrl } from '../assets.ts';
import { decodePixels } from '../ui/ip2/ip2.ts';

type WmdCore = Record<string, unknown> | null;
type WanimAttr = { target: number; field: number; sub: number; flags: number; kind: number; core: number; path: string };
type WanimChannel = { attr: number; mustContribute: boolean };
type WanimClip = { name: string; duration: number; channels: WanimChannel[] };
type Wanim = { attrs: WanimAttr[]; clips: WanimClip[] };
type BoundClip = unknown;
/** One clip's channels for blending, in the clip's order: (attribute index, curve), the curve
 * null for a channel without MustContribute. */
type BlendClip = { attr: number; curve: Curve | null }[];

/** A loaded mesh: the glTF scene as the template, base matrices, cores and clips. */
export type MeshTemplate = {
  path: string;
  gltf: GLTF;
  nodeCount: number;
  base: (THREE.Matrix4 | null)[];
  cores: WmdCore[];
  drawIndex: Map<number, number>;
  anim: Wanim | null;
  bound: Map<string, BoundClip>;
  /** Clips bound for layered blending (`poseLayers`). */
  blend: Map<string, BlendClip | null>;
  /** glTF material index -> material, for the materials the wanim's uniform attributes drive
   * (each instance gets its own copies of these). */
  animatedMaterials: Map<number, THREE.Material>;
};

/** One posed copy of a template. `fields` holds the last value written to each animated
 * attribute (the persistent `XAnimInstance` fields: an attribute no active clip writes keeps it).
 * `uniforms` holds the instance's material uniforms the wanim drives (glTF material index ->
 * uniform name -> value). */
export type MeshInstance = {
  template: MeshTemplate; root: THREE.Object3D; nodes: (THREE.Object3D | null)[]; fields: Map<number, number>;
  uniforms: Map<number, Record<string, THREE.Vector4>>;
};

const BLEND: Record<string, THREE.BlendingDstFactor> = {
  kBlendFactorZero: THREE.ZeroFactor, kBlendFactorOne: THREE.OneFactor, kBlendFactorDestColor: THREE.DstColorFactor,
  kBlendFactorOneMinusDestColor: THREE.OneMinusDstColorFactor, kBlendFactorSrcColor: THREE.SrcColorFactor,
  kBlendFactorOneMinusSrcColor: THREE.OneMinusSrcColorFactor, kBlendFactorSrcAlpha: THREE.SrcAlphaFactor,
  kBlendFactorOneMinusSrcAlpha: THREE.OneMinusSrcAlphaFactor, kBlendFactorDestAlpha: THREE.DstAlphaFactor,
  kBlendFactorOneMinusDestAlpha: THREE.OneMinusDstAlphaFactor,
};

type ShaderAttribute = { class?: string; Enable?: number | boolean; CompareFunction?: string; SourceFactor?: string; DestFactor?: string; CullMode?: string; RefValue?: number };

type UniformInstance = { Contents?: { Name?: string; Value?: number | number[] } };

/** `Root|SecondaryShaders|Trans_WMDWorm` (the worm's status tint): colour = mix(Emissive1,
 * WormtintColor, WormtintopacityColor × vec4(Texture0.rgb, 1)). The uniforms live per material
 * (`wormTint`), Texture0 is the tint mask of extras.wmd.Textures. */
const WORM_TINT = 'Root|SecondaryShaders|Trans_WMDWorm';
type TintUniforms = { WormtintColor: { value: THREE.Vector4 }; WormtintopacityColor: { value: THREE.Vector4 }; wormTintMask: { value: THREE.Texture | null } };
const wormTint = new WeakMap<THREE.Material, TintUniforms>();
const tintMasks = new Map<string, THREE.Texture>();

type WmdTexture = { PsParameterName?: string; TextureMap?: { Texture?: { png?: string } } };

function uniformValue(wmd: object, name: string): number[] | null {
  if (!('Uniforms' in wmd) || !Array.isArray(wmd.Uniforms)) return null;
  const v = (wmd.Uniforms as UniformInstance[]).find(u => u.Contents?.Name === name)?.Contents?.Value;
  return Array.isArray(v) ? v : null;
}

/** Gives a material the tint uniforms `u` and the Trans_WMDWorm shader patch. */
function installWormTint(m: THREE.Material, u: TintUniforms): void {
  wormTint.set(m, u);
  m.onBeforeCompile = shader => {
    Object.assign(shader.uniforms, u);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nuniform vec4 WormtintColor;\nuniform vec4 WormtintopacityColor;\nuniform sampler2D wormTintMask;')
      .replace('#include <map_fragment>', '#include <map_fragment>\n\tdiffuseColor = mix(diffuseColor, WormtintColor, WormtintopacityColor * vec4(texture2D(wormTintMask, vMapUv).rgb, 1.0));');
  };
  m.customProgramCacheKey = () => WORM_TINT;
}

/** A Trans_WMDWorm material: the tint uniforms with the material's defaults, the mask texture
 * (Texture0, shared between materials). */
function patchWormTint(m: THREE.Material, wmd: object): void {
  let mask: THREE.Texture | null = null;
  const png = ('Textures' in wmd && Array.isArray(wmd.Textures) ? (wmd.Textures as WmdTexture[]) : []).find(t => t.PsParameterName === 'Texture0')?.TextureMap?.Texture?.png;
  if (png) {
    mask = tintMasks.get(png) ?? null;
    if (!mask) {
      mask = new THREE.TextureLoader().load(assetUrl(png));
      mask.flipY = false;
      mask.colorSpace = THREE.NoColorSpace;
      mask.wrapS = mask.wrapT = THREE.RepeatWrapping;
      tintMasks.set(png, mask);
    }
  }
  installWormTint(m, {
    WormtintColor: { value: new THREE.Vector4(...(uniformValue(wmd, 'WormtintColor') ?? [1, 1, 1, 1])) },
    WormtintopacityColor: { value: new THREE.Vector4(...(uniformValue(wmd, 'WormtintopacityColor') ?? [0, 0, 0, 0])) },
    wormTintMask: { value: mask },
  });
}

/** The `*_UVt` shaders (`Root|BG|BG_Trans_Emis_UVt`, `Root|FX|PS_Trans_Emis_UVt`, ...): their
 * UVmover samples Emissive1 at uv + UvoffsetColor.xy + Speed1Color.xy × TimeTime. UvoffsetColor
 * is a per-instance uniform the wanim drives (the dubstep beam's FireBeam scrolls it 0 → -1: the
 * beam's own u spans [0, 0.5], which the texture leaves transparent at offset 0; the muzzle
 * flash's Go steps it through its four frames). No game-scene UVt material scrolls by
 * Speed1Color (only the HUD training movies do), so TimeTime is not driven. */
type UvtUniforms = { UvoffsetColor: { value: THREE.Vector4 } };
const uvt = new WeakMap<THREE.Material, UvtUniforms>();
const UVT = 'UVt';

/** Gives a material the UvoffsetColor uniform `u` and the UVmover shader patch. */
function installUvt(m: THREE.Material, u: UvtUniforms): void {
  uvt.set(m, u);
  m.onBeforeCompile = shader => {
    Object.assign(shader.uniforms, u);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nuniform vec4 UvoffsetColor;')
      .replace('#include <map_fragment>', THREE.ShaderChunk.map_fragment.replace('texture2D( map, vMapUv )', 'texture2D( map, vMapUv + UvoffsetColor.xy )'));
  };
  m.customProgramCacheKey = () => UVT;
}

/** The original material's render states (extras.wmd.ShaderAttributes), as the viewer applies
 * them, and the textures as raw values (the renderer works on the stored colour values). */
function applyStates(m: THREE.Material): void {
  const map = (m as THREE.MeshBasicMaterial).map;
  if (map) map.colorSpace = THREE.NoColorSpace;
  const wmd: unknown = m.userData?.wmd;
  if (!wmd || typeof wmd !== 'object' || !('ShaderAttributes' in wmd) || !Array.isArray(wmd.ShaderAttributes)) return;
  const lib = 'LibraryName' in wmd ? wmd.LibraryName : null;
  if (lib === WORM_TINT) patchWormTint(m, wmd);
  if (typeof lib === 'string' && lib.endsWith('_UVt')) installUvt(m, { UvoffsetColor: { value: new THREE.Vector4(...(uniformValue(wmd, 'UvoffsetColor') ?? [0, 0, 0, 1])) } });
  m.forceSinglePass = true;
  // Root.FX.PS_Trans_Emis(_UVt) without lights (the deferred renderer's light set is empty):
  // colour = TintcolourColor × Emissive1, alpha = Emissive1.a × OpacitycolourColor.r.
  // Root.BG.BG_Trans_Emis_UVt: colour = vertex colour × Emissive1, alpha = its alphas ×
  // OpacitycolourColor.r.
  if (lib === 'Root|FX|PS_Trans_Emis' || lib === 'Root|FX|PS_Trans_Emis_UVt') {
    const tint = uniformValue(wmd, 'TintcolourColor');
    if (tint) (m as THREE.MeshBasicMaterial).color?.setRGB(tint[0], tint[1], tint[2]);
  }
  if (lib === 'Root|FX|PS_Trans_Emis' || lib === 'Root|FX|PS_Trans_Emis_UVt' || lib === 'Root|BG|BG_Trans_Emis_UVt') {
    const opacity = uniformValue(wmd, 'OpacitycolourColor');
    if (opacity) m.opacity = opacity[0];
  }
  // Root.UI.UI_Trans_Emiss_Tinted (e.g. the jetpack's glow): Emissive1 × TintColor ×
  // BuildingfadeColor, alpha too (assets/shaders/glsl/Root.UI.UI_Trans_Emiss_Tinted.Frag.frag).
  if (lib === 'Root|UI|UI_Trans_Emiss_Tinted') {
    const tint = uniformValue(wmd, 'TintColor') ?? [1, 1, 1, 1], fade = uniformValue(wmd, 'BuildingfadeColor') ?? [1, 1, 1, 1];
    (m as THREE.MeshBasicMaterial).color?.setRGB(tint[0] * fade[0], tint[1] * fade[1], tint[2] * fade[2]);
    m.opacity = tint[3] * fade[3];
  }
  const attrs: ShaderAttribute[] = wmd.ShaderAttributes;
  for (const a of attrs) {
    if (a.class === 'XZBufferWriteEnable') m.depthWrite = !!a.Enable;
    else if (a.class === 'XDepthTest') m.depthTest = !!a.Enable && a.CompareFunction !== 'kCompareFunctionAlways';
    else if (a.class === 'XBlendModeGL') {
      const s = BLEND[a.SourceFactor ?? ''], d = BLEND[a.DestFactor ?? ''];
      if (s !== undefined && d !== undefined && !(s === THREE.OneFactor && d === THREE.ZeroFactor)) {
        m.transparent = true; m.blending = THREE.CustomBlending; m.blendSrc = s as THREE.BlendingSrcFactor; m.blendDst = d; m.blendEquation = THREE.AddEquation;
      }
    } else if (a.class === 'XCullFace') m.side = THREE.DoubleSide;
    else if (a.class === 'XAlphaTest' && a.Enable) m.alphaTest = Math.max(1e-6, (a.RefValue ?? 0) / ((a.RefValue ?? 0) > 1 ? 255 : 1));
  }
  m.needsUpdate = true;
}

/** glTF node index -> Object3D of `root` (a scene loaded or cloned from the same file). */
function nodeMap(gltf: GLTF, root: THREE.Object3D, original: THREE.Object3D): (THREE.Object3D | null)[] {
  const byOriginal = new Map<THREE.Object3D, THREE.Object3D>();
  const a: THREE.Object3D[] = [], b: THREE.Object3D[] = [];
  original.traverse(o => a.push(o));
  root.traverse(o => b.push(o));
  a.forEach((o, i) => byOriginal.set(o, b[i]));
  const out: (THREE.Object3D | null)[] = [];
  original.traverse(o => {
    const assoc = gltf.parser.associations.get(o);
    if (assoc && assoc.nodes !== undefined && out[assoc.nodes] === undefined) out[assoc.nodes] = byOriginal.get(o) ?? null;
  });
  return out;
}

const templates = new Map<string, Promise<MeshTemplate | null>>();

/** GLTFLoader's image loader for `loadTemplate`'s `decodeInWorker`: the PNG decoded by the UI
 * decoder worker (ui/ip2/decode.worker.ts: pixel buffers cross threads, never a worker's
 * ImageBitmap; docs/platform/UiFidelity.md) and uploaded as a data texture. GLTFLoader's own
 * image textures make WebKit decode on the main thread (`createImageBitmap` of the fetched PNG:
 * 150-330 ms per 2048x2048 texture of a front-end backdrop). The glTF images are converted PNG
 * files under assets/, `/assets/<path>`; the worker fetches them with the manifest's version. */
class WorkerImageLoader extends THREE.Loader<THREE.DataTexture> {
  load(url: string, onLoad: (t: THREE.DataTexture) => void, _onProgress?: (e: ProgressEvent) => void, onError?: (e: unknown) => void): void {
    const path = decodeURIComponent(new URL(url, location.href).pathname).replace(/^\/assets\//, '');
    decodePixels(path, [null]).then(([px]) => {
      if (!px) throw new Error(`${path}: not decoded`);
      // Straight alpha, rows top down: as GLTFLoader's image textures (flipY false).
      const t = new THREE.DataTexture(new Uint8Array(px.data.buffer), px.w, px.h);
      t.needsUpdate = true;
      onLoad(t);
    }).catch(e => onError?.(e));
  }
}

/** The loading manager that routes GLTFLoader's PNG images to WorkerImageLoader. */
let workerImages: THREE.LoadingManager | null = null;

/** Loads (once) the mesh at `glb` with its `.wanim`. `decodeInWorker`: its PNG textures decoded
 * off the main thread (WorkerImageLoader), for meshes loaded while the page is interactive
 * (the front-end backdrop). */
export function loadTemplate(glb: string, wanim: string | null, decodeInWorker = false): Promise<MeshTemplate | null> {
  let p = templates.get(glb);
  if (!p) {
    p = (async () => {
      try {
        const manager = decodeInWorker ? (workerImages ??= new THREE.LoadingManager().addHandler(/\.png$/i, new WorkerImageLoader())) : undefined;
        const gltf = await new GLTFLoader(manager).loadAsync(assetUrl(glb));
        const jn: { extras?: { wmd?: { Core?: { Matrix?: number[] } } }; matrix?: number[]; mesh?: number }[] = gltf.parser.json.nodes ?? [];
        const jm: { extras?: { wmd?: { draw_index?: number; Bounds?: number[] } } }[] = gltf.parser.json.meshes ?? [];
        const nodes = nodeMap(gltf, gltf.scene, gltf.scene);
        const base: (THREE.Matrix4 | null)[] = [];
        for (let i = 0; i < jn.length; i++) {
          const o = nodes[i];
          if (!o) { base[i] = null; continue; }
          const m = jn[i].extras?.wmd?.Core?.Matrix;
          o.matrixAutoUpdate = false;
          if (m && m.length === 12) o.matrix.fromArray([m[0], m[1], m[2], 0, m[3], m[4], m[5], 0, m[6], m[7], m[8], 0, m[9], m[10], m[11], 1]);
          else if (jn[i].matrix) o.matrix.fromArray(jn[i].matrix ?? []);
          else o.matrix.compose(o.position, o.quaternion, o.scale);
          base[i] = o.matrix.clone();
        }
        const cores: WmdCore[] = jn.map(n => (n.extras?.wmd?.Core ?? null) as WmdCore);
        const drawIndex = new Map<number, number>();
        jn.forEach((n, i) => { if (n.mesh !== undefined && nodes[i]) drawIndex.set(i, jm[n.mesh]?.extras?.wmd?.draw_index ?? 0); });
        // The shapes' bounding spheres are the XShape's own (`XNode::m_Bounds`, local centre and
        // radius): the layers sort transparent shapes back to front by the view z of that centre
        // (`XThreadedCullSortAction::AddShape` 0x1226350), and three.js by the bounding sphere's.
        jn.forEach((n, i) => {
          const b = n.mesh !== undefined ? jm[n.mesh]?.extras?.wmd?.Bounds : undefined;
          const o = nodes[i];
          if (!o || !b || b.length !== 4) return;
          for (const m of o instanceof THREE.Mesh ? [o] : o.children.filter(c => c instanceof THREE.Mesh)) {
            (m as THREE.Mesh).geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(b[0], b[1], b[2]), b[3]);
          }
        });
        gltf.scene.traverse(o => {
          if (o instanceof THREE.Mesh) for (const m of ([] as THREE.Material[]).concat(o.material)) applyStates(m);
          o.frustumCulled = false;
        });
        const anim: Wanim | null = wanim ? parseWanim(await assetBytes(wanim)) : null;
        // The materials the wanim's uniform attributes (`target` = glTF material index) drive.
        const animatedMaterials = new Map<number, THREE.Material>();
        const driven = new Set((anim?.attrs ?? []).filter(a => a.kind === KIND_UNIFORM).map(a => a.target));
        gltf.scene.traverse(o => {
          if (!(o instanceof THREE.Mesh)) return;
          for (const m of ([] as THREE.Material[]).concat(o.material)) {
            const i = gltf.parser.associations.get(m)?.materials;
            if (i !== undefined && driven.has(i)) animatedMaterials.set(i, m);
          }
        });
        return { path: glb, gltf, nodeCount: jn.length, base, cores, drawIndex, anim, bound: new Map(), blend: new Map(), animatedMaterials };
      } catch (e) {
        console.warn('mesh', glb, e);
        return null;
      }
    })();
    templates.set(glb, p);
  }
  return p;
}

/** An instance of a template: the node tree cloned (meshes share the template's geometry and
 * materials), except the materials the wanim drives, which each instance owns (their uniforms
 * are per instance, as the original's per-instance shader parameters). */
export function instantiate(t: MeshTemplate): MeshInstance {
  const root = cloneSkinned(t.gltf.scene);
  const nodes = nodeMap(t.gltf, root, t.gltf.scene);
  root.traverse(o => { o.matrixAutoUpdate = false; o.frustumCulled = false; });
  root.matrixAutoUpdate = true;
  const uniforms = new Map<number, Record<string, THREE.Vector4>>();
  if (t.animatedMaterials.size) {
    const copies = new Map<THREE.Material, THREE.Material>();
    for (const [i, m] of t.animatedMaterials) {
      const c = m.clone();
      const u = wormTint.get(m);
      if (u) {
        const own: TintUniforms = { WormtintColor: { value: u.WormtintColor.value.clone() }, WormtintopacityColor: { value: u.WormtintopacityColor.value.clone() }, wormTintMask: u.wormTintMask };
        installWormTint(c, own);
        uniforms.set(i, { WormtintColor: own.WormtintColor.value, WormtintopacityColor: own.WormtintopacityColor.value });
      }
      const v = uvt.get(m);
      if (v) {
        const own: UvtUniforms = { UvoffsetColor: { value: v.UvoffsetColor.value.clone() } };
        installUvt(c, own);
        uniforms.set(i, { ...uniforms.get(i), UvoffsetColor: own.UvoffsetColor.value });
      }
      copies.set(m, c);
    }
    root.traverse(o => {
      if (!(o instanceof THREE.Mesh)) return;
      o.material = Array.isArray(o.material) ? o.material.map(m => copies.get(m) ?? m) : copies.get(o.material) ?? o.material;
    });
  }
  return { template: t, root, nodes, fields: new Map(), uniforms };
}

/** Poses an instance with clip `clip` at `time` (base pose without a clip); returns whether the clip exists. */
export function pose(inst: MeshInstance, clip: string | null, time: number): boolean {
  const t = inst.template;
  inst.nodes.forEach((o, i) => { const b = t.base[i]; if (o && b) o.matrix.copy(b); });
  let found = false;
  if (t.anim && clip) {
    let bound = t.bound.get(clip);
    if (bound === undefined) {
      const c = t.anim.clips.find(x => x.name === clip);
      bound = c ? bindClip(t.anim, c) : null;
      t.bound.set(clip, bound);
    }
    if (bound) {
      found = true;
      for (const [target, m] of poseAt(bound, t.cores, time) as Iterable<[number, number[][]]>) {
        const o = inst.nodes[target];
        if (!o) continue;
        const [r0, r1, r2, tr] = m;
        o.matrix.fromArray([r0[0], r0[1], r0[2], 0, r1[0], r1[1], r1[2], 0, r2[0], r2[1], r2[2], 0, tr[0], tr[1], tr[2], 1]);
      }
    }
  }
  return found;
}

/** An active clip state of a mesh instance (`XAnimInstance` state slot): clip, time, weight. */
export type AnimLayer = { clip: string; time: number; weight: number };

/** `wanim` attribute flags (assetconv: XAnimAttribute): averaged by the total weight, or the
 * largest contribution instead of the sum. */
const ATTR_AVERAGED = 0x8, ATTR_USE_MAX = 0x10;

/** The channels of clip `name` of a template for blending (cached), null for no such clip. */
function blendClip(t: MeshTemplate, name: string): BlendClip | null {
  let b = t.blend.get(name);
  if (b === undefined) {
    const c = t.anim?.clips.find(x => x.name === name);
    b = c ? c.channels.map(ch => ({ attr: ch.attr, curve: ch.mustContribute ? new Curve(ch) : null })) : null;
    t.blend.set(name, b);
  }
  return b;
}

const f32 = Math.fround;

/** One `XAnimInstance::Evaluate`'s input: the states it blends, in its order (its active slots),
 * and the clips with a weight that hold no slot (`claimed`). */
export type AnimEval = { layers: AnimLayer[]; claimed: string[] };

/** The attributes a MustContribute channel of a clip targets (the ones it writes). */
const clipWrites = new WeakMap<BlendClip, number[]>();
function writesOf(b: BlendClip): number[] {
  let w = clipWrites.get(b);
  if (!w) {
    w = [...new Set(b.filter(c => c.curve).map(c => c.attr))];
    clipWrites.set(b, w);
  }
  return w;
}

/** The fields one evaluation writes, as `Evaluate` 0x11ee600 / `EvalCurves` 0x11ee370 /
 * `ReevaluateWriteQuery` 0x11ee4b0 / `WriteFields` 0x11ec4e0 compute them. A weight of 0 is
 * skipped. Per state, per channel of its clip, from a value of 0 for the first blended state
 * (`bStore`) and the running value after it: a MustContribute channel adds weight × its curve at
 * the state's time (a use-max attribute takes the larger of the curve and the running value,
 * unweighted) and the weight to the attribute's count (use-max: only for the first state);
 * another channel keeps the value and counts the weight only for the first state. The curve takes
 * any time: outside its keys the channel's pre/post infinity (`CurveEvaluationCache::EvaluateAt`).
 * Written are the attributes a MustContribute channel of a state with a weight targets (claimed
 * clips, unblended, write theirs too): value / count for an averaged attribute with a count, else
 * the value. Float32 arithmetic. `found` collects the clip names that exist. */
function evaluate(t: MeshTemplate, anim: Wanim, e: AnimEval, found: Set<string> | null): Map<number, number> {
  const value = new Map<number, number>(), count = new Map<number, number>(), written = new Set<number>();
  let first = true;
  for (const l of e.layers) {
    if (l.weight === 0) continue;
    const b = blendClip(t, l.clip);
    if (!b) continue;
    found?.add(l.clip);
    const w = f32(l.weight);
    for (const { attr, curve } of b) {
      const prev = first ? 0 : value.get(attr) ?? 0;
      if (curve) {
        written.add(attr);
        const v = f32(curve.evaluate(f32(l.time)));
        if (anim.attrs[attr].flags & ATTR_USE_MAX) {
          value.set(attr, v <= prev ? prev : v);
          if (first) count.set(attr, f32((count.get(attr) ?? 0) + w));
        } else {
          value.set(attr, f32(prev + f32(v * w)));
          count.set(attr, f32((count.get(attr) ?? 0) + w));
        }
      } else {
        value.set(attr, prev);
        if (first) count.set(attr, f32((count.get(attr) ?? 0) + w));
      }
    }
    first = false;
  }
  for (const c of e.claimed) for (const { attr, curve } of blendClip(t, c) ?? []) if (curve) written.add(attr);
  const out = new Map<number, number>();
  for (const attr of written) {
    const v = value.get(attr) ?? 0, n = count.get(attr) ?? 0;
    out.set(attr, n !== 0 && anim.attrs[attr].flags & ATTR_AVERAGED ? f32(v / n) : v);
  }
  return out;
}

/** Poses an instance with consecutive evaluations of its `XAnimInstance` states, oldest first:
 * the original evaluates an instance once per frame, and an attribute no weighted state of a
 * frame writes keeps the value last written to it (the instance's persistent fields), so every
 * step's states count, not only the last ones (crates/sim/src/present/mesh/anim_instance.rs).
 * Worked newest first: an evaluation is computed only when it writes an attribute no later one
 * writes. Transform cores and uniforms are then rebuilt from the fields. Returns the clip names
 * of the newest evaluation that exist. */
export function poseSteps(inst: MeshInstance, evals: AnimEval[]): Set<string> {
  const t = inst.template;
  const found = new Set<string>();
  const anim = t.anim;
  if (anim && evals.length) {
    const covered = new Set<number>(), fields = new Map<number, number>();
    let lastKey: string | null = null;
    for (let i = evals.length - 1; i >= 0 && covered.size < anim.attrs.length; i--) {
      const e = evals[i];
      // The clips that write: an evaluation with the same clips as the one after it writes the
      // same attributes, all covered already.
      const clips = e.layers.filter(l => l.weight !== 0).map(l => l.clip).concat(e.claimed);
      const key = clips.join('\u0000');
      if (key === lastKey) continue;
      lastKey = key;
      let fresh = false;
      for (const c of clips) {
        const b = blendClip(t, c);
        if (b && writesOf(b).some(a => !covered.has(a))) { fresh = true; break; }
      }
      if (!fresh && i !== evals.length - 1) continue;
      for (const [attr, v] of evaluate(t, anim, e, i === evals.length - 1 ? found : null)) {
        if (covered.has(attr)) continue;
        covered.add(attr);
        fields.set(attr, v);
      }
    }
    for (const [attr, v] of fields) inst.fields.set(attr, v);
  }
  inst.nodes.forEach((o, i) => { const b = t.base[i]; if (o && b) o.matrix.copy(b); });
  if (!anim || !inst.fields.size) return found;
  const byTarget = new Map<number, { core: string; f: Record<string, unknown> }>();
  for (const [attr, v] of inst.fields) {
    const a = anim.attrs[attr];
    if (a.kind === KIND_UNIFORM) {
      // "Material|UniformC" (C the component letter, `sub` its index): the instance's uniform.
      const name = a.path.slice(a.path.indexOf('|') + 1, -1);
      const u = inst.uniforms.get(a.target)?.[name];
      if (u) u.setComponent(a.sub, v);
      continue;
    }
    const core = CORE_CLASSES[a.core];
    if (a.kind !== KIND_CORE || !core || a.core === 5) continue;
    const base = t.cores[a.target];
    if (!base) continue;
    let e = byTarget.get(a.target);
    if (!e) {
      const f: Record<string, unknown> = {};
      for (const k of Object.keys(base)) f[k] = Array.isArray(base[k]) ? (base[k] as number[]).slice() : base[k];
      e = { core, f };
      byTarget.set(a.target, e);
    }
    const comp = e.f[coreFieldName(core, a.field) ?? ''];
    if (Array.isArray(comp)) comp[a.sub] = v;
  }
  for (const [target, { core, f }] of byTarget) {
    const o = inst.nodes[target];
    if (!o) continue;
    const [r0, r1, r2, tr] = localMatrix(core, f) as number[][];
    o.matrix.fromArray([r0[0], r0[1], r0[2], 0, r1[0], r1[1], r1[2], 0, r2[0], r2[1], r2[2], 0, tr[0], tr[1], tr[2], 1]);
  }
  return found;
}

/** [`poseSteps`] with one evaluation (meshes the client drives itself once per frame: the GUI
 * meshes, the UI XOM items). */
export function poseLayers(inst: MeshInstance, layers: AnimLayer[], claimed: string[] = []): Set<string> {
  return poseSteps(inst, [{ layers, claimed }]);
}

/** The clips of a template's wanim. */
export function clipNames(t: MeshTemplate): string[] {
  return t.anim ? t.anim.clips.map(c => c.name) : [];
}

// --- resource lookup ----------------------------------------------------------------------------

type IndexResource = { name?: string; glb?: string; wanim?: string; clips?: [string, number, number?][] };
type XomIndex = { archives: Record<string, { resources: IndexResource[] }> };

let index: Map<string, Map<string, IndexResource>> | null = null;

/** xom/index.json as source -> resource name -> entry. */
export async function loadIndex(): Promise<void> {
  const x = await assetJson<XomIndex>('xom/index.json');
  index = new Map();
  for (const [src, a] of Object.entries(x.archives)) {
    const m = new Map<string, IndexResource>();
    for (const r of a.resources) if (r.name && r.glb) m.set(r.name, r);
    index.set(src, m);
  }
}

/** The bundles every match loads (BaseMesh_MATCH_BUNDLES of crates/sim/src/present/mesh/base_mesh.rs). */
const MATCH_BUNDLES = ['DataLinux/Bundles/Startup.bdl', 'DataLinux/Bundles/Core.bdl', 'DataLinux/Bundles/Font.bdl', 'DataLinux/Bundles/SuperBundle.bdl', 'DataLinux/Bundles/In-Game.bdl'];

/** The glb/wanim of mesh `name`: in `source` when given, else in the match bundles and the theme's. */
export function findMesh(name: string, source: string | null, theme: string | null): { glb: string; wanim: string | null } | null {
  if (!index) return null;
  const order = source ? [source] : [...MATCH_BUNDLES, ...(theme ? [`DataLinux/Bundles/${theme}.bdl`] : [])];
  for (const src of order) {
    const r = index.get(src)?.get(name);
    if (r?.glb) return { glb: r.glb, wanim: r.wanim ?? null };
  }
  return null;
}

/** The clips (name, seconds) xom/index.json lists for mesh `name` of `source`, in clip-list order
 * (`XMeshInstance::GetAnimDuration` by index), or null before the index loaded / for no entry.
 * Synchronous, unlike the template: the UI timelines' HASXOMFINISHED waits read it every tick. */
export function indexClips(name: string, source: string): { name: string; duration: number }[] | null {
  const r = index?.get(source)?.get(name);
  return r?.clips ? r.clips.map(([n, d]) => ({ name: n, duration: d })) : null;
}
